// Privacy Policy and Terms of Use shown at /privacy and /terms.
// The App Store and Google Play listings link to these URLs, so keep the paths stable.
// When the substance changes, bump LEGAL_EFFECTIVE_DATE.

export const LEGAL_CONTACT_EMAIL = 'contact@venuereportcard.com';
export const LEGAL_EFFECTIVE_DATE = 'October 27, 2026';

export const PRIVACY_POLICY = {
  id: 'privacy',
  eyebrow: 'Legal',
  title: 'Privacy Policy',
  intro:
    'Venue Report Card LLC ("VRC", "we", "us") runs Venue Report Card, a venue database and touring toolkit for performers and crew. This policy explains what we collect when you use the VRC website and the VRC apps for iPhone and Android, why we collect it, who we share it with, and the choices you have.',
  sections: [
    {
      id: 'collect',
      heading: 'What we collect',
      paragraphs: ['We only collect what we need to run VRC.'],
      bullets: [
        'Account details: your name, email address and password when you sign up. Passwords are handled by Firebase Authentication; we never see them in plain text.',
        'Profile details you choose to add, such as a display name, profile photo, bio, band or organization, and links.',
        'Content you create: venue reports and grades, venue specs and photos, community posts, gear swap and event listings, comments, messages in tour party chats, and touring data such as tour dates, day sheets, schedules, setlists, riders, guest lists, contacts, merch counts, expenses and settlements.',
        'Payment details: when you subscribe, donate or receive funds, Stripe processes your card or bank details. We receive a customer reference, plan and payment status, never your full card or bank details. If you receive payouts or tour funds, Stripe also collects the identity and bank details it needs to verify you and pay you out.',
        'Location: when you allow it, your device location is used to center the map and find nearby venues. We do not track your location in the background.',
        'Usage and device data: pages viewed, features used, app version, browser or device type, approximate region and crash or error details, collected through Google Analytics for Firebase and our hosting logs.',
        'Messages you send us, such as Contact form submissions and email.',
      ],
    },
    {
      id: 'use',
      heading: 'How we use it',
      bullets: [
        'To run your account and the features you use, including sharing tours and calendars with the people you invite.',
        'To show venue information, reports and community content to other users.',
        'To process subscriptions, donations, tour funds and payouts.',
        'To send account, security and service emails, and notifications you turn on, such as tour chat and notify-me emails.',
        'To keep VRC safe: preventing spam, abuse and fraud, and moderating content.',
        'To understand how VRC is used so we can fix problems and improve it.',
      ],
      paragraphs: ['We do not sell your personal information, and we do not use it for third-party advertising.'],
    },
    {
      id: 'public',
      heading: 'What other people can see',
      paragraphs: [
        'Venue reports, grades, venue specs and photos, community posts and listings, and your public profile can be seen by other VRC users and, for venue pages, by anyone visiting the site. Do not put anything in your public profile you do not want other users to see.',
        'Tour data is private to you and the tour members you invite. Anyone with a share link you create can view what that link shares until you turn it off.',
      ],
    },
    {
      id: 'share',
      heading: 'Who we share it with',
      paragraphs: ['We share personal information only with service providers who help us run VRC, under their own privacy and security terms:'],
      bullets: [
        'Google Firebase and Google Cloud (accounts, database, file storage, hosting, cloud functions, analytics).',
        'Stripe (payments, subscriptions and payouts).',
        'Twilio SendGrid (email delivery).',
        'Google Maps and Mapbox (maps, places and directions). Map searches and coordinates are sent to them to draw maps and routes.',
        'OpenAI, only when someone uses an AI feature. Formatting an imported rider sends that rider\'s text. Smart people search sends the search words and the public profile bios being searched, which can include yours. Under OpenAI\'s API terms this data is not used to train their models.',
        'Ticketmaster (venue names and coordinates, to show upcoming events), and Open-Meteo and RainViewer (map coordinates, to show weather and radar).',
      ],
      paragraphsAfter: [
        'We may also share information if the law requires it, to protect the safety or rights of our users or the public, or as part of a merger or sale of the service, in which case this policy continues to apply.',
      ],
    },
    {
      id: 'retention',
      heading: 'How long we keep it',
      paragraphs: [
        'We keep your account information for as long as your account is open. When you delete your account we delete or anonymize your personal information within 30 days, except where we must keep records for legal, tax or payment reasons (for example Stripe transaction records). Venue specs you contributed may stay in the venue database without your name.',
      ],
    },
    {
      id: 'choices',
      heading: 'Your choices and rights',
      bullets: [
        'Access and update your profile at any time in Account.',
        'Turn off location access in your browser or phone settings.',
        'Turn off optional emails, such as notify-me and tour chat emails, by emailing us.',
        'Request a copy of your data, a correction, or deletion of your account and personal data by emailing us from the address on your account.',
      ],
      paragraphs: [
        `To delete your account, email ${LEGAL_CONTACT_EMAIL} with the subject "Delete my account" from the email address you signed up with. We confirm and complete the request within 30 days. Depending on where you live (for example California, the EU or the UK) you may have further rights, and you can complain to your local data protection authority. We will not treat you differently for using them.`,
      ],
    },
    {
      id: 'security',
      heading: 'Security',
      paragraphs: [
        'Data is encrypted in transit (HTTPS) and stored with Google Cloud, which encrypts it at rest. Access is limited to people who need it to run VRC. No system is perfectly secure, so please use a strong, unique password.',
      ],
    },
    {
      id: 'children',
      heading: 'Children',
      paragraphs: [
        'VRC is for people 13 and older, and you must be 18 or older to receive payouts or tour funds. We do not knowingly collect information from children under 13. If you believe a child has given us information, contact us and we will delete it.',
      ],
    },
    {
      id: 'international',
      heading: 'Where data is processed',
      paragraphs: [
        'VRC is run from the United States and our providers may process data in the United States and other countries. By using VRC you understand your information may be processed outside your country.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to this policy',
      paragraphs: [
        'We will post any changes on this page and update the effective date. If a change is significant, we will tell you in the app or by email before it takes effect.',
      ],
    },
    {
      id: 'contact',
      heading: 'Contact us',
      paragraphs: [`Questions about privacy or your data: ${LEGAL_CONTACT_EMAIL}.`],
    },
  ],
};

export const TERMS_OF_USE = {
  id: 'terms',
  eyebrow: 'Legal',
  title: 'Terms of Use',
  intro:
    'These terms are an agreement between you and Venue Report Card LLC ("VRC", "we", "us") for use of the VRC website and the VRC apps for iPhone and Android. By creating an account or using VRC, you agree to them and to our Privacy Policy.',
  sections: [
    {
      id: 'eligibility',
      heading: 'Who can use VRC',
      paragraphs: [
        'You must be at least 13 years old, and old enough to agree to these terms where you live. Receiving payouts or tour funds requires you to be 18 or older. If you use VRC for a band, crew or company, you confirm you may accept these terms for them.',
      ],
    },
    {
      id: 'account',
      heading: 'Your account',
      paragraphs: [
        'Give accurate information, keep your password private, and tell us right away if you think someone else has used your account. You are responsible for what happens under your account. You can stop using VRC at any time and ask us to delete your account as described in the Privacy Policy.',
      ],
    },
    {
      id: 'venue-info',
      heading: 'Venue information',
      paragraphs: [
        'VRC is not associated with any venue. Venue specs come from venue websites, tech packets and the community, and can be out of date or wrong. Always advance your show directly with the venue. You use venue information at your own risk.',
      ],
    },
    {
      id: 'content',
      heading: 'Your content',
      paragraphs: [
        'You keep ownership of what you post: reports, grades, photos, posts, listings, messages and tour data. You give VRC a worldwide, non-exclusive, royalty-free license to host, store, display, reproduce and adapt it (for example resizing a photo) as needed to run and promote VRC. For venue reports and specs, this license continues after you delete your account so the venue database stays useful; we remove your name from them.',
        'You confirm you have the rights to what you post and that it is honest and based on your own experience.',
      ],
    },
    {
      id: 'conduct',
      heading: 'Community rules',
      paragraphs: ['Do not use VRC to:'],
      bullets: [
        'Post anything unlawful, hateful, harassing, threatening, sexually explicit, or that targets a person.',
        'Post private or sensitive information, such as venue staff emails, wifi passwords, door codes or someone else\'s personal details, without permission.',
        'Post false or misleading reviews, including reviews of venues you own or work for without saying so.',
        'Infringe anyone\'s copyright, trademark or other rights.',
        'Spam, scrape, or send automated traffic; probe or break our security; or interfere with other users.',
        'Buy or sell anything illegal or unsafe through gear swap or events.',
      ],
      paragraphsAfter: [
        `We may remove content and suspend or close accounts that break these rules. To report content or a user, email ${LEGAL_CONTACT_EMAIL} with a link to it; we review reports and act on objectionable content within 24 hours.`,
      ],
    },
    {
      id: 'paid',
      heading: 'Subscriptions and payments',
      paragraphs: [
        'Some features need a paid VRC Member subscription. Prices are shown before you pay. Subscriptions renew automatically each month (or each year on a yearly plan) until you cancel, and you can cancel any time in Manage Subscription; access continues until the end of the period you paid for. Except where the law requires, payments are non-refundable.',
        'Payments, donations, tour funds and payouts are processed by Stripe and are also subject to Stripe\'s terms. Credits have no cash value except as VRC describes in the app. We may change prices with notice before your next renewal.',
      ],
    },
    {
      id: 'tours',
      heading: 'Tours, shared data and money between members',
      paragraphs: [
        'When you invite people to a tour, they can see and, depending on the permissions you give them, edit that tour\'s data. Settlements, expenses, per diems and tour fund balances in VRC are tools to help you keep track; they are not financial, tax or legal advice, and VRC is not a party to agreements between you, your bandmates, venues or promoters.',
      ],
    },
    {
      id: 'ours',
      heading: 'Our service',
      paragraphs: [
        'VRC, its design, software, logos and the venue database as a whole belong to us and our licensors. We give you a personal, non-transferable right to use VRC under these terms. We are always improving VRC, so features can change, and we may pause or stop part of the service.',
      ],
    },
    {
      id: 'third-party',
      heading: 'Third-party services and links',
      paragraphs: [
        'VRC uses and links to services we do not control, such as maps, payment processors and venue websites. Their terms and privacy policies apply to your use of them.',
      ],
    },
    {
      id: 'disclaimer',
      heading: 'Disclaimers',
      paragraphs: [
        'VRC is provided "as is" and "as available". To the fullest extent the law allows, we make no warranties, express or implied, including that VRC or its information will be accurate, complete, uninterrupted or error-free.',
      ],
    },
    {
      id: 'liability',
      heading: 'Limitation of liability',
      paragraphs: [
        'To the fullest extent the law allows, VRC will not be liable for indirect, incidental, special, consequential or punitive damages, or for lost profits, revenue, data or shows, arising from your use of VRC. Our total liability for any claim is limited to the greater of the amount you paid us in the 12 months before the claim or US $100. Some places do not allow these limits, so they may not apply to you.',
      ],
    },
    {
      id: 'indemnity',
      heading: 'Indemnity',
      paragraphs: [
        'If your content or your breach of these terms leads to a claim against us, you agree to cover our reasonable losses and costs, to the extent the law allows.',
      ],
    },
    {
      id: 'ending',
      heading: 'Ending these terms',
      paragraphs: [
        'You can stop using VRC and delete your account at any time. We may suspend or end your access if you break these terms or if we must for legal reasons. Sections that by their nature should survive (such as content licenses for venue reports, disclaimers and limits of liability) continue after termination.',
      ],
    },
    {
      id: 'app-stores',
      heading: 'App Store and Google Play',
      paragraphs: [
        'If you use VRC on iPhone or iPad, these terms are between you and VRC, not Apple. Apple is not responsible for the app or its support, and has no warranty obligation beyond refunding the purchase price if the law requires it. Apple and its subsidiaries are third-party beneficiaries of these terms and may enforce them. Your use must also follow the App Store and Google Play terms.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to these terms',
      paragraphs: [
        'We may update these terms. We will post changes here with a new effective date and, for significant changes, tell you in the app or by email. Continuing to use VRC after a change means you accept the updated terms.',
      ],
    },
    {
      id: 'contact',
      heading: 'Contact us',
      paragraphs: [`Questions about these terms: ${LEGAL_CONTACT_EMAIL}.`],
    },
  ],
};
